import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { getCommonInitialConfig } from '@app/app.module';
import {
    AVIS_VERIFIER_AGE,
    AvisVerifiesGetAverageBody,
    AvisVerifiesGetReviews,
    AvisVerifiesGetReviewsBody,
    AvisVerifiesGetReviewsRoot,
    Review,
} from '@app/interfaces/api/avis-verifies.interface';
import { ReviewInfosModel } from '@app/models/review-infos.model';
import { ReviewUserModel } from '@app/models/review-user.model';
import { ReviewModel } from '@app/models/review.model';

import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class ReviewRepository {
    private apiBaseUrl: string;
    private websiteId: string;
    private cachedReviewInfos: { [key: string]: ReviewInfosModel } = {};

    private cachedRawReviews: { [key: string]: Review[] } = {};

    private http: HttpClient = inject(HttpClient);

    public constructor() {
        const config = getCommonInitialConfig().global.avisVerifies;
        this.apiBaseUrl = config.baseUrl;
        this.websiteId = config.idWebsite;
    }

    public getProductsAverage(gencodes: string[]) {
        const defaultParams: AvisVerifiesGetReviews = {
            idWebsite: this.websiteId,
            query: 'average',
            plateforme: 'fr',
            product: gencodes,
        };

        return this.http.post<AvisVerifiesGetAverageBody>(`${this.apiBaseUrl}/product`, defaultParams);
    }

    public getProductReview(productId: string, offset = 0, limit = 10) {
        const defaultParams: AvisVerifiesGetReviews = {
            idWebsite: this.websiteId,
            query: 'reviews',
            plateforme: 'fr',
            product: [productId],
        };

        let initialReview: Observable<ReviewInfosModel>;
        if (!this.cachedReviewInfos[productId]) {
            // Full Review API call to calculate average
            const httpParams = new HttpParams({ fromObject: defaultParams as any });
            initialReview = this.http
                .get<AvisVerifiesGetReviewsBody>(`${this.apiBaseUrl}/product`, { params: httpParams })
                .pipe(
                    map((result) => {
                        if (result?.length > 0) {
                            this.cachedRawReviews[productId] = result[0].reviews;
                            return this.transformToReviewDetail(result[0]);
                        }

                        throw new Error('Error');
                    }),
                    tap((result) => {
                        this.cachedReviewInfos[productId] = result;
                    }),
                );
        } else {
            initialReview = of(this.cachedReviewInfos[productId]);
        }

        return initialReview.pipe(
            map((reviewInfo) => {
                // Simulate pagination
                reviewInfo.reviews = this.getReviews(productId, offset, limit);
                return reviewInfo;
            }),
        );
    }

    public getReviews(productId: string, offset: number = 0, limit: number = null): ReviewModel[] {
        const allRawReviews = this.cachedRawReviews[productId];
        const reviews = limit ? allRawReviews.slice(offset, limit + offset) : allRawReviews;

        return reviews.map((review) => this.transformToReviewModel(review));
    }

    private transformToReviewDetail(data: AvisVerifiesGetReviewsRoot): ReviewInfosModel {
        const reviewInfo = new ReviewInfosModel();
        const reviewCount = data.reviews.length;
        let note = 0;
        let averageAuto = 0;
        let averageUse = 0;
        let averageDesign = 0;
        let averageMulti = 0;

        reviewInfo.reviewCount = reviewCount;

        // calculate averages
        data.reviews.forEach((reviewData) => {
            const review = this.transformToReviewModel(reviewData);
            reviewInfo.addReview(review);
            note += +reviewData.rate;
            averageAuto += +reviewData.product_review_autonomie || 0;
            averageUse += +reviewData.product_review_facilitedutilisation || 0;
            averageDesign += +reviewData.product_review_design || 0;
            averageMulti += +reviewData.product_review_qualite_appareil2 || 0;
        });

        reviewInfo.note = this.calculateAverage(note, reviewCount);

        reviewInfo.averages = {
            multimedia: { label: 'Multimedia', note: this.calculateAverage(averageMulti, reviewCount) },
            use: { label: 'Utilisation', note: this.calculateAverage(averageUse, reviewCount) },
            autonomy: { label: 'Autonomie', note: this.calculateAverage(averageAuto, reviewCount) },
            design: { label: 'Design', note: this.calculateAverage(averageDesign, reviewCount) },
        };

        return reviewInfo;
    }

    private calculateAverage(score: number, count: number): number {
        return Math.round((score * 10) / count) / 10;
    }

    private transformToReviewModel(reviewData: Review): ReviewModel {
        const user = new ReviewUserModel();
        user.login = reviewData.brand_review_pseudo || reviewData.firstname;
        user.gender = null;
        user.verified = true; // all reviews are verified
        user.ageLabel = reviewData.brand_review_age;

        if (reviewData.product_review_sexe) {
            user.gender = reviewData.product_review_sexe === 'Homme' ? 'Male' : 'Female';
        }

        const review = new ReviewModel();
        review.user = user;
        review.note = +reviewData.rate;
        review.created_at = new Date(reviewData.publish_date);
        review.shortMessage = reviewData.review.slice(0, 280);
        review.message = reviewData.review;
        review.title = reviewData.product_review_title;

        // review.syndication = { // @todo mapping
        //     name: 'Syndication',
        //     image: 'https://www.1min30.com/wp-content/uploads/2018/04/logo-Bouygues.jpg'
        // };

        review.recommendedFor = reviewData.product_review_recommandation_telephone;
        review.recommanded = reviewData.product_review_recommandation_ami === 'Oui';
        return review;
    }

    private getUserAge(age: AVIS_VERIFIER_AGE): number {
        const ageMapping = {
            [AVIS_VERIFIER_AGE.LESS17]: 10,
            [AVIS_VERIFIER_AGE.BETWEEN1824]: 20,
            [AVIS_VERIFIER_AGE.BETWEEN2534]: 30,
            [AVIS_VERIFIER_AGE.BETWEEN3544]: 40,
            [AVIS_VERIFIER_AGE.BETWEEN4554]: 50,
            [AVIS_VERIFIER_AGE.BETWEEN5564]: 60,
            [AVIS_VERIFIER_AGE.MORE64]: 70,
        };

        return ageMapping[age];
    }
}
